import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { getBadgeDefinition, getInitialState } from "./data";
import type {
  YouScreenState,
  YouScreenAction,
  PersonalMedicalData,
  DateFormat,
  Language,
  TimeFormat,
  UserPreferences,
} from "./types";
import {
  usePatient,
  useUpdatePatient,
} from "@src/packages/patients/hooks/usePatient";
import type { UpdatePatientPayload } from "@src/packages/patients/patients.types";
import type { Patient as BasePatient } from "@louise/medical-history/basic-info/calculateBasicInfoCompletion";
import { useStytch } from "../../../utils/useStytch";
import { getAuthCallbackUrl } from "../../../utils/getAuthCallbackUrl";
import {
  usePreferences,
  useUpdatePreferences,
} from "@src/packages/preferences";
import type { Preferences as ApiPreferences } from "@src/packages/preferences";
import {
  useNotificationPreferences,
  useUpdateNotificationPreferences,
  isNotificationPreferenceEnabled,
  type NotificationPreferencesData,
  type NotificationPreferencesPatch,
  type SupportedNotificationPreferenceField,
} from "@src/packages/notifications";
import { usePrivacy, useUpdatePrivacy } from "@src/packages/privacy";
import { useFeedback } from "@louise/moon-ui";
import { useLocale } from "@src/packages/i18n";
import {
  usePartnerInvitation,
  useSendPartnerInvite,
} from "@src/packages/patients/hooks/usePartnerInvite";
import type { SharingCategory } from "@src/packages/patients/hooks/usePatientSharing";
import { usePartnerSharingScope } from "@src/packages/patients/hooks/usePartnerSharingScope";
import type { PrivacyData } from "@src/packages/privacy";
import { useBadgeUnlockEvents } from "@src/packages/badges/useBadgeUnlockEvents";
import {
  preferEarlierBadgeUnlockEvent,
  type BadgeUnlockEvent,
} from "@src/packages/badges/badge-events";

// The OpenAPI spec now includes the emergency-contact columns, so the
// generated Patient type carries them directly.
type Patient = BasePatient;

/** UI toggle IDs that map to API notification-preferences fields */
const NOTIFICATION_API_MAP: Record<
  string,
  SupportedNotificationPreferenceField
> = {
  appointments: "appointmentsEnabled",
  "cycle-updates": "cycleUpdates",
  "test-results": "testResults",
  "medication-reminders": "medicationsEnabled",
  "coverage-status": "coverageStatus",
  documents: "documentsEnabled",
};

type NotificationPreferenceWrite = {
  value: NotificationPreferencesData;
  patch: NotificationPreferencesPatch;
};

function createNotificationTogglePatch(
  field: SupportedNotificationPreferenceField,
  value: boolean,
): NotificationPreferencesPatch {
  return { [field]: value };
}

type YouScreenContextValue = {
  state: YouScreenState;
  dispatch: React.Dispatch<YouScreenAction>;
  /** Server-backed badge history availability. Local estimates are never used. */
  badgeDataStatus: "loading" | "ready" | "unavailable";
  /** Patient data from the API (null when loading or unauthenticated) */
  patient: Patient | null | undefined;
  /** Whether patient data is currently loading */
  patientLoading: boolean;
  /** personalMedical derived from API patient data (falls back to empty defaults) */
  personalMedical: PersonalMedicalData;
  /** Effective preferences from /me/preferences, with safe UI defaults while loading. */
  preferences: UserPreferences;
  /** Load/save state for the persisted preferences surface. */
  preferencesStatus: {
    isLoading: boolean;
    hasLoadError: boolean;
    hasSaveError: boolean;
    retryLoad: () => void;
    retrySave: () => void;
  };
  /** Notification preferences from /me/notification-preferences API */
  notificationPreferences: NotificationPreferencesData | null | undefined;
  /** Load state for the persisted notification summary/settings data. */
  notificationPreferencesStatus: {
    isLoading: boolean;
    hasLoadError: boolean;
    hasSaveError: boolean;
    retryLoad: () => void;
    retrySave: () => void;
  };
  /** Privacy settings from /me/privacy API */
  privacy: PrivacyData | null | undefined;
  /** API-backed anonymous research-contribution preference. */
  researchConsent: {
    value: boolean;
    hasLoadError: boolean;
    hasSaveError: boolean;
  };
  /** Partner invite mutation state */
  partnerInvite: {
    isSending: boolean;
    isSuccess: boolean;
    error: string | null;
    resetError: () => void;
    scopeReady: boolean;
    isEditing: boolean;
    initialSharingCategories?: SharingCategory[];
  };
};

const YouScreenContext = createContext<YouScreenContextValue | null>(null);

function reducer(
  state: YouScreenState,
  action: YouScreenAction,
): YouScreenState {
  switch (action.type) {
    case "TOGGLE_MED_REMINDER":
      return {
        ...state,
        medicationReminders: state.medicationReminders.map((m) =>
          m.id === action.id ? { ...m, enabled: !m.enabled } : m,
        ),
      };
    case "SET_SELECTED_ANSWER":
      return { ...state, selectedAnswer: action.label };
    case "SHOW_MODAL":
      return {
        ...state,
        activeModal: action.modal,
        ...(action.modal === null && { intentPickerGenderOverride: null }),
        ...(action.modal === null && {
          deleteConfirmText: "",
          partnerInvitePhone: "",
          partnerInviteName: "",
        }),
      };
    case "SET_INTENT_PICKER_GENDER_OVERRIDE":
      return { ...state, intentPickerGenderOverride: action.gender };
    case "SHOW_DATA_SHARING_MODAL":
      return { ...state, dataSharingModal: action.connectionType };
    case "SET_DELETE_CONFIRM_TEXT":
      return { ...state, deleteConfirmText: action.text };
    case "SET_PARTNER_INVITE_PHONE":
      return { ...state, partnerInvitePhone: action.phone };
    case "SET_PARTNER_INVITE_NAME":
      return { ...state, partnerInviteName: action.name };
    case "SEND_PARTNER_INVITE":
      return {
        ...state,
        connections: state.connections.map((c) =>
          c.type === "partner"
            ? {
                ...c,
                name: action.name,
                status: "pending" as const,
                detail: action.phone,
              }
            : c,
        ),
      };
    case "TOGGLE_DATA_PERMISSION":
      return {
        ...state,
        connections: state.connections.map((c) =>
          c.type === action.connectionType
            ? {
                ...c,
                permissions: {
                  ...c.permissions,
                  [action.permission]: !c.permissions[action.permission],
                },
              }
            : c,
        ),
      };
    case "SET_ACTIVE_PANEL":
      return { ...state, activePanel: action.panel };
    case "CLOSE_PANEL":
      return { ...state, activePanel: null };
    case "SET_PERSONAL_FIELD":
      return {
        ...state,
        personalMedical: {
          ...state.personalMedical,
          [action.field]: action.value,
        },
      };
    case "SET_INSURANCE_FIELD":
      return {
        ...state,
        insuranceInfo: {
          ...state.insuranceInfo,
          [action.field]: action.value,
        },
      };
    case "SET_LANGUAGE":
      return {
        ...state,
        preferences: { ...state.preferences, language: action.language },
      };
    case "SET_DATE_FORMAT":
      return {
        ...state,
        preferences: { ...state.preferences, dateFormat: action.format },
      };
    case "SET_TIMEZONE":
      return {
        ...state,
        preferences: {
          ...state.preferences,
          timezone: action.timezone,
          timezoneLocation: action.timezoneLocation ?? null,
        },
      };
    case "SET_HAPTICS":
      return {
        ...state,
        preferences: {
          ...state.preferences,
          hapticsEnabled: action.value,
        },
      };
    case "SET_SOUND":
      return {
        ...state,
        preferences: {
          ...state.preferences,
          soundEnabled: action.value,
        },
      };
    case "REMOVE_PARTNER":
      return {
        ...state,
        connections: state.connections.map((c) =>
          c.type === "partner"
            ? { ...c, name: null, status: "not_connected" as const }
            : c,
        ),
      };
    default:
      return state;
  }
}

/**
 * Map API Patient data to the PersonalMedicalData shape used by UI components.
 * Returns empty defaults when patient is null/undefined (loading or unauthenticated).
 */
function derivePersonalMedical(
  patient: Patient | null | undefined,
  stytchPhone?: string | null,
): PersonalMedicalData {
  if (!patient) {
    return {
      firstName: null,
      lastName: null,
      email: null,
      phone: null,
      dateOfBirth: null,
      gender: null,
      emergencyContact: null,
      bloodType: null,
      allergies: [],
      hasAllergies: null,
      address: null,
    };
  }

  // Build address string from the first address record if available
  const addr = patient.addresses?.[0];
  const addressParts = [
    addr?.address1,
    addr?.address2,
    addr?.city,
    addr?.postalCode,
    addr?.country,
  ].filter(Boolean);
  const addressStr = addressParts.length > 0 ? addressParts.join(", ") : null;

  return {
    firstName: patient.firstName ?? null,
    lastName: patient.lastName ?? null,
    email: patient.email ?? null,
    phone: patient.phoneNumber ?? stytchPhone ?? null,
    dateOfBirth: patient.dateOfBirth ?? null,
    gender: patient.gender ?? null,
    emergencyContact: patient.emergencyContactName
      ? {
          name: patient.emergencyContactName,
          phone: patient.emergencyContactPhone ?? "",
          relationship: patient.emergencyContactRelationship ?? "",
        }
      : null,
    bloodType: patient.bloodType ?? null,
    allergies: getAllergyLabels(patient.medicalHistory?.allergies),
    hasAllergies: patient.medicalHistory?.hasAllergies ?? null,
    address: addressStr,
  };
}

type AllergyRecord = {
  name?: string | null;
  other?: string | null;
  allergy?: string | null;
};

function getAllergyLabels(allergies: AllergyRecord[] | null | undefined) {
  return (allergies ?? [])
    .map((allergy) => {
      const name = allergy.name?.trim() || allergy.other?.trim();
      if (name) return name;
      return allergy.allergy ? allergy.allergy.replace(/_/g, " ") : null;
    })
    .filter((label): label is string => Boolean(label));
}

function buildPersonalFieldPayload(
  action: Extract<YouScreenAction, { type: "SET_PERSONAL_FIELD" }>,
): UpdatePatientPayload | null {
  switch (action.field) {
    case "firstName":
      return { firstName: action.value };
    case "lastName":
      return { lastName: action.value };
    case "email":
      return { email: action.value ?? "" };
    case "phone":
      return { phoneNumber: action.value };
    case "dateOfBirth":
      return { dateOfBirth: action.value };
    case "gender":
      return action.value === "AMAB" ||
        action.value === "AFAB" ||
        action.value === null
        ? { gender: action.value }
        : null;
    case "bloodType": {
      const allowed = [
        "A+",
        "A-",
        "B+",
        "B-",
        "AB+",
        "AB-",
        "O+",
        "O-",
      ] as const;
      if (action.value === null) return { bloodType: null };
      return (allowed as readonly string[]).includes(action.value)
        ? { bloodType: action.value as (typeof allowed)[number] }
        : null;
    }
    case "address":
      return null;
  }
}

const DATE_FORMATS: readonly DateFormat[] = [
  "MM/DD/YYYY",
  "DD/MM/YYYY",
  "YYYY-MM-DD",
];
const TIME_FORMATS: readonly TimeFormat[] = ["12H", "24H"];

function normalizeLanguage(language: string | null | undefined): Language {
  // The API historically defaulted to regional codes such as `en-US`, while
  // the app currently ships English and French translation bundles. Keep the
  // persisted API value broad, but never hand an unsupported locale to i18n.
  return language?.trim().toLowerCase().startsWith("fr") ? "fr" : "en";
}

function normalizePreferences(
  preferences: ApiPreferences | null | undefined,
  fallback: UserPreferences,
): UserPreferences {
  if (!preferences) return fallback;

  return {
    language: normalizeLanguage(preferences.language),
    timezone: preferences.timezone || fallback.timezone,
    timezoneLocation: preferences.timezoneLocation ?? null,
    dateFormat: DATE_FORMATS.includes(preferences.dateFormat as DateFormat)
      ? (preferences.dateFormat as DateFormat)
      : fallback.dateFormat,
    timeFormat: TIME_FORMATS.includes(preferences.timeFormat as TimeFormat)
      ? (preferences.timeFormat as TimeFormat)
      : fallback.timeFormat,
    hapticsEnabled: preferences.hapticsEnabled,
    soundEnabled: preferences.soundEnabled,
  };
}

export function YouScreenProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(reducer, undefined, getInitialState);

  // Fetch patient data from API (safe for unauthenticated users — query is disabled when IDs are missing)
  const { data: patient, isLoading: patientLoading } = usePatient();
  const updatePatientMutation = useUpdatePatient();
  const stytch = useStytch();

  // Preferences API
  const {
    data: storedPreferences,
    isLoading: preferencesLoading,
    error: preferencesError,
    refetch: refetchPreferences,
  } = usePreferences();
  const updatePreferencesMutation = useUpdatePreferences();
  const { setSoundEnabled, setHapticsEnabled } = useFeedback();
  const { locale, setLocale } = useLocale();
  const [optimisticPreferences, setOptimisticPreferences] =
    useState<UserPreferences | null>(null);
  const [preferencesSaveError, setPreferencesSaveError] = useState(false);
  const preferencesQueue = useRef<{
    next: UserPreferences | null;
    inFlight: UserPreferences | null;
  }>({ next: null, inFlight: null });
  const failedPreferences = useRef<UserPreferences | null>(null);
  const effectivePreferencesRef = useRef<UserPreferences>(state.preferences);
  const flushPreferencesRef = useRef<() => void>(() => {});

  const serverPreferences = useMemo(
    () =>
      normalizePreferences(storedPreferences, {
        ...state.preferences,
        // Until the API answers, preserve the app's persisted locale instead
        // of overwriting it with the static English fallback.
        language: locale,
      }),
    [locale, state.preferences, storedPreferences],
  );
  const preferences = optimisticPreferences ?? serverPreferences;
  effectivePreferencesRef.current = preferences;

  const flushPreferences = useCallback(() => {
    const queue = preferencesQueue.current;
    if (queue.inFlight !== null || queue.next === null) return;

    const next = queue.next;
    queue.inFlight = next;
    updatePreferencesMutation.mutate(next, {
      onSuccess: () => {
        // Do not clear an optimistic value that has already been superseded by
        // a later tap. The final desired value will be sent next.
        if (queue.next === next) {
          queue.next = null;
          failedPreferences.current = null;
          setOptimisticPreferences(null);
          setPreferencesSaveError(false);
        }
      },
      onError: () => {
        // Surface a failure only when it applies to the patient's latest
        // choice. A newer queued choice should remain responsive and retry
        // against the last confirmed server state.
        if (queue.next === next) {
          queue.next = null;
          failedPreferences.current = next;
          setOptimisticPreferences(null);
          setPreferencesSaveError(true);
        }
      },
      onSettled: () => {
        queue.inFlight = null;
        flushPreferencesRef.current();
      },
    });
  }, [updatePreferencesMutation]);
  flushPreferencesRef.current = flushPreferences;

  const queuePreferences = useCallback((patch: Partial<UserPreferences>) => {
    const base =
      preferencesQueue.current.next ??
      preferencesQueue.current.inFlight ??
      effectivePreferencesRef.current;
    const next = { ...base, ...patch };

    preferencesQueue.current.next = next;
    failedPreferences.current = null;
    setPreferencesSaveError(false);
    setOptimisticPreferences(next);
    flushPreferencesRef.current();
  }, []);

  const retryLoadPreferences = useCallback(() => {
    void refetchPreferences();
  }, [refetchPreferences]);

  const retrySavePreferences = useCallback(() => {
    const failed = failedPreferences.current;
    if (!failed) return;

    preferencesQueue.current.next = failed;
    failedPreferences.current = null;
    setPreferencesSaveError(false);
    setOptimisticPreferences(failed);
    flushPreferencesRef.current();
  }, []);

  // Sync the derived server or optimistic setting into the app-wide feedback
  // and locale providers. On a rejected write, clearing the optimistic record
  // makes this effect restore the last confirmed preference automatically.
  useEffect(() => {
    void setLocale(preferences.language);
    setSoundEnabled(preferences.soundEnabled);
    setHapticsEnabled(preferences.hapticsEnabled);
  }, [
    preferences.language,
    preferences.soundEnabled,
    preferences.hapticsEnabled,
    setSoundEnabled,
    setHapticsEnabled,
    setLocale,
  ]);

  const preferencesStatus = useMemo(
    () => ({
      isLoading: preferencesLoading,
      hasLoadError: Boolean(preferencesError),
      hasSaveError: preferencesSaveError,
      retryLoad: retryLoadPreferences,
      retrySave: retrySavePreferences,
    }),
    [
      preferencesLoading,
      preferencesError,
      preferencesSaveError,
      retryLoadPreferences,
      retrySavePreferences,
    ],
  );

  // Notification preferences API. These use the same serialized optimistic
  // writer as general preferences: rapid taps should stay responsive, but
  // reach the server in order and return to the last confirmed value on error.
  const notificationPreferencesQuery = useNotificationPreferences();
  const {
    data: storedNotificationPreferences,
    isError: notificationPreferencesError,
    isLoading: notificationPreferencesLoading,
    refetch: refetchNotificationPreferences,
  } = notificationPreferencesQuery;
  const updateNotificationMutation = useUpdateNotificationPreferences();
  const [
    optimisticNotificationPreferences,
    setOptimisticNotificationPreferences,
  ] = useState<NotificationPreferencesData | null>(null);
  const [
    notificationPreferencesSaveError,
    setNotificationPreferencesSaveError,
  ] = useState(false);
  const notificationPreferencesQueue = useRef<{
    queued: NotificationPreferenceWrite | null;
    inFlight: NotificationPreferenceWrite | null;
  }>({ queued: null, inFlight: null });
  const failedNotificationPreferencesPatch =
    useRef<NotificationPreferencesPatch | null>(null);
  const effectiveNotificationPreferencesRef =
    useRef<NotificationPreferencesData | null>(null);
  const flushNotificationPreferencesRef = useRef<() => void>(() => {});

  const notificationPreferences =
    optimisticNotificationPreferences ?? storedNotificationPreferences;
  effectiveNotificationPreferencesRef.current = notificationPreferences ?? null;

  const flushNotificationPreferences = useCallback(() => {
    const queue = notificationPreferencesQueue.current;
    if (queue.inFlight || !queue.queued) return;

    const write = queue.queued;
    queue.queued = null;
    queue.inFlight = write;
    updateNotificationMutation.mutate(write.patch, {
      onSuccess: () => {
        if (!queue.queued) {
          failedNotificationPreferencesPatch.current = null;
          setOptimisticNotificationPreferences(null);
          setNotificationPreferencesSaveError(false);
        }
      },
      onError: () => {
        // A newer preference is still meaningful after an earlier write
        // fails, so only surface the error when it affects the latest choice.
        if (!queue.queued) {
          failedNotificationPreferencesPatch.current = write.patch;
          setOptimisticNotificationPreferences(null);
          setNotificationPreferencesSaveError(true);
        }
      },
      onSettled: () => {
        queue.inFlight = null;
        flushNotificationPreferencesRef.current();
      },
    });
  }, [updateNotificationMutation]);
  flushNotificationPreferencesRef.current = flushNotificationPreferences;

  const queueNotificationPreferences = useCallback(
    (patch: NotificationPreferencesPatch) => {
      const queue = notificationPreferencesQueue.current;
      const base =
        queue.queued?.value ??
        queue.inFlight?.value ??
        effectiveNotificationPreferencesRef.current;
      if (!base) return;

      const next = { ...base, ...patch };
      queue.queued = {
        value: next,
        // Repeat the in-flight preference values with the final queued
        // change. If an earlier write fails, this final write still expresses
        // every choice the patient made rather than silently losing the
        // earlier toggle.
        patch: {
          ...(queue.inFlight?.patch ?? {}),
          ...(queue.queued?.patch ?? {}),
          ...patch,
        },
      };
      failedNotificationPreferencesPatch.current = null;
      setNotificationPreferencesSaveError(false);
      setOptimisticNotificationPreferences(next);
      flushNotificationPreferencesRef.current();
    },
    [],
  );

  const retryLoadNotificationPreferences = useCallback(() => {
    void refetchNotificationPreferences();
  }, [refetchNotificationPreferences]);
  const retrySaveNotificationPreferences = useCallback(() => {
    const failed = failedNotificationPreferencesPatch.current;
    if (!failed) return;

    failedNotificationPreferencesPatch.current = null;
    setNotificationPreferencesSaveError(false);
    queueNotificationPreferences(failed);
  }, [queueNotificationPreferences]);
  const notificationPreferencesStatus = useMemo(
    () => ({
      isLoading: notificationPreferencesLoading,
      hasLoadError: Boolean(notificationPreferencesError),
      hasSaveError: notificationPreferencesSaveError,
      retryLoad: retryLoadNotificationPreferences,
      retrySave: retrySaveNotificationPreferences,
    }),
    [
      notificationPreferencesError,
      notificationPreferencesLoading,
      notificationPreferencesSaveError,
      retryLoadNotificationPreferences,
      retrySaveNotificationPreferences,
    ],
  );

  // Privacy API
  const { data: privacy, error: privacyError } = usePrivacy();
  const updatePrivacyMutation = useUpdatePrivacy();
  const [optimisticResearchConsent, setOptimisticResearchConsent] = useState<
    boolean | null
  >(null);
  const researchConsentQueue = useRef<{
    next: boolean | null;
    inFlight: boolean | null;
  }>({ next: null, inFlight: null });
  const flushResearchConsentRef = useRef<() => void>(() => {});

  // Persist rapid changes in order without making the control wait for the
  // network. The first requested value starts immediately; while it is saving,
  // later taps collapse to the patient's final chosen value.
  const flushResearchConsent = useCallback(() => {
    const queue = researchConsentQueue.current;
    if (queue.inFlight !== null || queue.next === null) return;

    const next = queue.next;
    queue.inFlight = next;
    updatePrivacyMutation.mutate(
      { participateInResearch: next },
      {
        onSuccess: () => {
          if (queue.next === next) {
            queue.next = null;
            setOptimisticResearchConsent(null);
          }
        },
        onError: () => {
          if (queue.next === next) {
            queue.next = null;
            setOptimisticResearchConsent(null);
          }
        },
        onSettled: () => {
          queue.inFlight = null;
          flushResearchConsentRef.current();
        },
      },
    );
  }, [updatePrivacyMutation]);
  flushResearchConsentRef.current = flushResearchConsent;

  const queueResearchConsent = useCallback((next: boolean) => {
    researchConsentQueue.current.next = next;
    setOptimisticResearchConsent(next);
    flushResearchConsentRef.current();
  }, []);

  const researchConsent = useMemo(
    () => ({
      value:
        optimisticResearchConsent ?? privacy?.participateInResearch ?? false,
      hasLoadError: Boolean(privacyError),
      // Suppress an error from an earlier request while a newer patient choice
      // is still queued. The final request will surface its own failure.
      hasSaveError:
        Boolean(updatePrivacyMutation.error) &&
        optimisticResearchConsent === null,
    }),
    [
      privacy?.participateInResearch,
      optimisticResearchConsent,
      privacyError,
      updatePrivacyMutation.error,
    ],
  );

  // Badge state is reconstructed only from server-produced badge events. A
  // page mount, derived patient field, or local counter cannot unlock a badge.
  const badgeEventsQuery = useBadgeUnlockEvents();
  const {
    events: badgeUnlockEvents,
    isError: badgeEventsError,
    isLoading: badgeEventsLoading,
    isFetched: badgeEventsFetched,
    hasMalformedEvent: badgeEventsMalformed,
    isFixtureMode: badgeEventsFixtureMode,
  } = badgeEventsQuery;
  const serverBadges = useMemo(() => {
    const unlocksByBadgeId = new Map<string, BadgeUnlockEvent>();

    for (const envelope of badgeUnlockEvents) {
      const current = unlocksByBadgeId.get(envelope.event.badgeId);
      unlocksByBadgeId.set(
        envelope.event.badgeId,
        current
          ? preferEarlierBadgeUnlockEvent(current, envelope.event)
          : envelope.event,
      );
    }

    return state.badges.map((badge) => {
      const unlock = unlocksByBadgeId.get(badge.id);
      return unlock
        ? {
            ...badge,
            unlocked: true,
            unlockedAt: unlock.occurredAt,
            description: unlock.copy.body,
          }
        : { ...badge, unlocked: false, unlockedAt: undefined };
    });
  }, [badgeUnlockEvents, state.badges]);
  const badgeEventsUnsupported = badgeUnlockEvents.some(
    ({ event }) => getBadgeDefinition(event.badgeId) === null,
  );

  const badgeDataStatus: YouScreenContextValue["badgeDataStatus"] =
    badgeEventsError ||
    badgeEventsMalformed ||
    badgeEventsUnsupported ||
    (!patientLoading && !patient && !badgeEventsFixtureMode)
      ? "unavailable"
      : badgeEventsLoading || !badgeEventsFetched
        ? "loading"
        : "ready";
  const visibleState = useMemo(
    () => ({ ...state, badges: serverBadges }),
    [serverBadges, state],
  );

  // Partner invite API
  const sendPartnerInviteMutation = useSendPartnerInvite();
  const { data: partnerInvitation } = usePartnerInvitation();
  const {
    scope: partnerSharingScope,
    isAvailable: isPartnerSharingScopeAvailable,
  } = usePartnerSharingScope();

  // Derive personalMedical from API data, falling back to Stytch for phone
  const stytchPhone =
    stytch.user.getSync()?.phone_numbers?.[0]?.phone_number ?? null;
  const personalMedical = useMemo(
    () => derivePersonalMedical(patient, stytchPhone),
    [patient, stytchPhone],
  );

  // Wrap dispatch to intercept API-backed actions and persist to their endpoints
  const wrappedDispatch: React.Dispatch<YouScreenAction> = useCallback(
    (action: YouScreenAction) => {
      // ── Personal fields → Patient API ──
      if (action.type === "SET_PERSONAL_FIELD" && patient) {
        const payload = buildPersonalFieldPayload(action);
        if (payload) {
          updatePatientMutation.mutate(payload);
        }
        if (action.field === "email" && action.value) {
          const callbackUrl = getAuthCallbackUrl();
          stytch.magicLinks.email
            .send(action.value, {
              login_magic_link_url: callbackUrl,
              signup_magic_link_url: callbackUrl,
            })
            .catch(() => {});
        }
        if (payload) return;
      }

      // ── Address (structured) → Patient API ──
      if (action.type === "SET_ADDRESS" && patient) {
        const existingId = patient.addresses?.[0]?.id;
        const addressPayload: UpdatePatientPayload = {
          addresses: [
            {
              ...(existingId ? { id: existingId } : {}),
              address1: action.data.address1,
              address2: action.data.address2,
              city: action.data.city,
              postalCode: action.data.postalCode,
              country: action.data.country,
            },
          ],
        };
        updatePatientMutation.mutate(addressPayload);
        return;
      }

      // ── Preferences → /me/preferences API ──
      if (action.type === "SET_LANGUAGE") {
        queuePreferences({ language: action.language });
        return;
      }
      if (action.type === "SET_DATE_FORMAT") {
        queuePreferences({ dateFormat: action.format });
        return;
      }
      if (action.type === "SET_TIMEZONE") {
        queuePreferences({
          timezone: action.timezone,
          timezoneLocation: action.timezoneLocation ?? null,
        });
        return;
      }
      if (action.type === "SET_SOUND") {
        queuePreferences({ soundEnabled: action.value });
        return;
      }
      if (action.type === "SET_HAPTICS") {
        queuePreferences({ hapticsEnabled: action.value });
        return;
      }

      // ── Reminder timings → /me/notification-preferences API ──
      if (action.type === "SET_REMINDER_TIMINGS") {
        if (action.timings.length === 0) {
          // The API requires at least one timing. Clearing the final chip
          // therefore turns off that reminder while retaining its last valid
          // timing for the next time it is enabled.
          queueNotificationPreferences(
            action.context === "medications"
              ? { medicationsEnabled: false }
              : { appointmentsEnabled: false },
          );
          return;
        }

        queueNotificationPreferences(
          action.context === "medications"
            ? { medicationTimings: action.timings }
            : { appointmentTimings: action.timings },
        );
        return;
      }

      // ── Notification toggles → /me/notification-preferences API ──
      if (action.type === "TOGGLE_NOTIFICATION") {
        const apiField = NOTIFICATION_API_MAP[action.id];
        if (!apiField || !notificationPreferences) return;

        queueNotificationPreferences(
          createNotificationTogglePatch(
            apiField,
            !isNotificationPreferenceEnabled(notificationPreferences, apiField),
          ),
        );
        return;
      }

      // ── Privacy → /me/privacy API ──
      if (action.type === "SET_ANONYMOUS_DATA") {
        queueResearchConsent(action.value);
        return;
      }

      // ── Partner invite → /partner/invite API ──
      if (action.type === "SEND_PARTNER_INVITE") {
        // The invitation query is the source of truth for pending state. Do
        // not update the reducer optimistically: a failed request must not
        // leave Connections claiming that an invite was sent.
        if (sendPartnerInviteMutation.isPending || !partnerSharingScope) return;
        sendPartnerInviteMutation.mutate({
          phone: action.phone,
          firstName: action.name,
          sharingCategories: action.sharingCategories,
          sharingScope: partnerSharingScope,
          replaceExisting: partnerInvitation?.status === "pending",
        });
        return;
      }

      // All other actions pass through to reducer
      dispatch(action);
    },
    [
      dispatch,
      patient,
      updatePatientMutation,
      stytch.magicLinks.email,
      notificationPreferences,
      queueNotificationPreferences,
      queueResearchConsent,
      sendPartnerInviteMutation,
      partnerInvitation?.status,
      partnerSharingScope,
      queuePreferences,
    ],
  );

  const partnerInvite = useMemo(
    () => ({
      isSending: sendPartnerInviteMutation.isPending,
      isSuccess: sendPartnerInviteMutation.isSuccess,
      error: sendPartnerInviteMutation.error?.message ?? null,
      resetError: sendPartnerInviteMutation.reset,
      scopeReady: isPartnerSharingScopeAvailable,
      isEditing: partnerInvitation?.status === "pending",
      initialSharingCategories:
        partnerInvitation?.status === "pending"
          ? partnerInvitation.sharingCategories
          : undefined,
    }),
    [
      sendPartnerInviteMutation.isPending,
      sendPartnerInviteMutation.isSuccess,
      sendPartnerInviteMutation.error,
      sendPartnerInviteMutation.reset,
      isPartnerSharingScopeAvailable,
      partnerInvitation?.status,
      partnerInvitation?.sharingCategories,
    ],
  );

  const value = useMemo(
    () => ({
      state: visibleState,
      dispatch: wrappedDispatch,
      badgeDataStatus,
      patient,
      patientLoading,
      personalMedical,
      preferences,
      preferencesStatus,
      notificationPreferences: notificationPreferences ?? undefined,
      notificationPreferencesStatus,
      privacy,
      researchConsent,
      partnerInvite,
    }),
    [
      visibleState,
      badgeDataStatus,
      wrappedDispatch,
      patient,
      patientLoading,
      personalMedical,
      preferences,
      preferencesStatus,
      notificationPreferences,
      notificationPreferencesStatus,
      privacy,
      researchConsent,
      partnerInvite,
    ],
  );

  return (
    <YouScreenContext.Provider value={value}>
      {children}
    </YouScreenContext.Provider>
  );
}

export function useYouScreen() {
  const ctx = useContext(YouScreenContext);
  if (!ctx) {
    throw new Error("useYouScreen must be used within YouScreenProvider");
  }
  return ctx;
}
